import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import { getFleetTopology, linkTopologyNode, queueFleetCommand, getFleetCommands, openFleetStream, cancelFleetCommand } from "../api/updateApi";
import { getHosts } from "../api/hostApi";
import { Hero, Dropdown, ToggleSwitch, EmptyState } from "../components/adminSurface/AdminSurface";
import { ServerArt } from "../components/adminSurface/heroArt";
import ConfirmModal from "../components/common/ConfirmModal";
import "../styles/fleetTopology.css";

// The agent heartbeats every 30s, so polling faster than that only re-renders the same numbers.
const POLL_MS = 30000;

function fmtAbs(raw) {
  if (!raw) return "";
  try {
    const d = new Date(raw);
    if (isNaN(d)) return String(raw);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  } catch { return String(raw); }
}

export default function FleetTopologyPage() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [data, setData] = useState(null);
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [fetchedAt, setFetchedAt] = useState(null);

  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [offlineOnly, setOfflineOnly] = useState(false);

  // { node, hostId, hostName, unlink } — null when no dialog is open.
  const [pendingLink, setPendingLink] = useState(null);
  const [linking, setLinking] = useState(false);

  // { machine, service, command } — the action awaiting confirmation.
  const [pendingCmd, setPendingCmd] = useState(null);
  const [sending, setSending] = useState(false);
  const [commands, setCommands] = useState([]);

  // Guards against setting state after unmount. It MUST be re-armed on mount, not just cleared on
  // unmount: StrictMode mounts, unmounts and remounts in development, so a ref that is only ever set
  // to false stays false for the life of the page and every load() bails before it can store anything.
  // The symptom is a page stuck on "Loading…" for ever, in dev only.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const load = useCallback(async (showSpinner) => {
    if (showSpinner) setLoading(true);
    try {
      const [body, log] = await Promise.all([
        getFleetTopology(token),
        // The command log is what turns "I pressed restart" into "and here is what happened".
        getFleetCommands(token, { limit: 60 }).catch(() => ({ commands: [] })),
      ]);
      if (!alive.current) return;
      setData(body);
      setCommands(log?.commands || []);
      setFetchedAt(new Date());
      setError("");
    } catch (e) {
      if (alive.current) setError(e?.message || String(e));
    } finally {
      if (alive.current && showSpinner) setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(true); }, [load]);

  // Host names, so a suggested link can say WHICH machine it would attach to rather than an id.
  useEffect(() => {
    let on = true;
    getHosts(token).then((h) => { if (on) setHosts(Array.isArray(h) ? h : []); }).catch(() => {});
    return () => { on = false; };
  }, [token]);

  // Quiet background refresh: no spinner, so the page does not flicker every half minute.
  // This is the safety net and it is NEVER turned off, including while the change stream is connected.
  useEffect(() => {
    const id = setInterval(() => load(false), POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  // Optional accelerator. The server says whether it is switched on (Fleet:LiveUpdates), so a
  // deployment that leaves it off makes no request that could only fail. The stream carries no data:
  // it says the fleet moved, and the poll's own loader does the reading. If it drops, is blocked by a
  // proxy, or is never enabled, the page behaves exactly as it did before it existed - just slower to
  // notice. Reconnects on close, because the server ends long-lived streams deliberately.
  const liveUpdates = !!data?.liveUpdates;
  useEffect(() => {
    if (!liveUpdates) return undefined;

    let closed = false;
    let close = null;
    let retry = null;

    const connect = () => {
      if (closed) return;
      try {
        close = openFleetStream(token, {
          onChange: () => load(false),
          onError: () => {
            if (closed) return;
            // Deliberately unhurried: nothing is waiting on this, and a tight reconnect loop against a
            // proxy that is refusing the stream would be a self-inflicted load.
            retry = setTimeout(connect, 15000);
          },
        });
      } catch {
        // An accelerator that cannot start must leave the page exactly as it would have been without
        // one. Swallowing this is the whole reason the poll above is never turned off.
        close = null;
      }
    };
    connect();

    return () => {
      closed = true;
      if (retry) clearTimeout(retry);
      close?.();
    };
  }, [liveUpdates, token, load]);

  const hostName = useCallback(
    (id) => hosts.find((h) => (h.id ?? h.Id) === id)?.hostName
         ?? hosts.find((h) => (h.id ?? h.Id) === id)?.HostName
         ?? `#${id}`,
    [hosts]);

  const machines = useMemo(() => {
    const all = data?.machines || [];
    const q = search.trim().toLowerCase();
    return all.filter((m) => {
      if (stateFilter && m.state !== stateFilter) return false;
      if (offlineOnly && m.online) return false;
      if (!q) return true;
      return [m.displayName, m.nodeName, m.machineName, m.hostName, m.address, m.installRoot,
              ...(m.components || [])]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [data, search, stateFilter, offlineOnly]);

  const s = data?.summary;
  // Machines before filtering: an empty grid means something different when the installation has none
  // at all than when the search box simply excluded them.
  const total = data?.machines?.length || 0;

  async function applyLink() {
    if (!pendingLink) return;
    setLinking(true);
    try {
      await linkTopologyNode(token, {
        nodeId: pendingLink.node.nodeId,
        hostConfigurationId: pendingLink.unlink ? null : pendingLink.hostId,
      });
      setInfo(pendingLink.unlink
        ? t("fleetTopology.unlinked", { name: pendingLink.node.displayName })
        : t("fleetTopology.linked", { name: pendingLink.node.displayName, host: pendingLink.hostName }));
      setPendingLink(null);
      await load(false);
    } catch (e) {
      setError(e?.message || String(e));
      setPendingLink(null);
    } finally {
      setLinking(false);
    }
  }

  async function runCommand() {
    if (!pendingCmd) return;
    const { machine, service, command } = pendingCmd;
    setSending(true);
    try {
      await queueFleetCommand(token, {
        nodeId: machine.nodeId ?? undefined,
        hostId: machine.nodeId ? undefined : (machine.hostId ?? undefined),
        command,
        argument: service,
      });
      setInfo(t("fleetTopology.commandQueued", { command: t(`fleetTopology.cmd${command}`), service }));
      setPendingCmd(null);
      // Straight away, so the row appears as Pending rather than after the next poll.
      await load(false);
    } catch (e) {
      setError(e?.message || String(e));
      setPendingCmd(null);
    } finally {
      setSending(false);
    }
  }

  // Withdrawing a queued command is the undo, not a destructive act, so it is not put behind a
  // confirmation. The server refuses anything already picked up, which is why the button only appears
  // on rows that are still Pending.
  async function withdraw(id) {
    try {
      await cancelFleetCommand(token, id);
      setInfo(t("fleetTopology.commandWithdrawn"));
      await load(false);
    } catch (e) {
      setError(e?.message || String(e));
    }
  }

  return (
    <div className="app-root">
      <div className="nav-strip"><NavBar /></div>
      <HeaderBar />

      <div className="page-container" style={{ paddingBottom: 40 }}>
        <Hero title={t("fleetTopology.title")} subtitle={t("fleetTopology.subtitle")}
          statValue={data ? String(s?.machines ?? 0) : "—"} statLabel={t("fleetTopology.statMachines")}
          statDotColor={s && s.agents > 0 && s.online === s.agents ? "#4ade80" : "#fbbf24"}
          onRefresh={() => load(true)} refreshing={loading} art={<ServerArt />} />

        {info ? (
          <div className="asf-alert" style={{ color: "#15803d", background: "#dcfce7", borderColor: "#bbf7d0" }}>
            ✓ {info}
          </div>
        ) : null}
        {error ? <div className="asf-alert"><span style={{ fontSize: 16 }}>⚠</span> {error}</div> : null}

        {/* The one thing this page must never do is stay quiet about why it is thin. If the server
            ANSWERING this request has no agent row, no machine in the installation will have one, every
            card will be built from worker rows alone, and no machine can be acted on. Saying so here is
            the difference between a page that looks broken and a page that names its own gap. */}
        {data && data.thisServer && data.thisServer.agentPresent === false ? (
          <div className="asf-alert" style={{ color: "#92400e", background: "#fef3c7", borderColor: "#fde68a" }}>
            <span style={{ fontSize: 16 }}>⚠</span>
            <span>
              <b>{t("fleetTopology.noAgentTitle")}</b>{" "}
              {t("fleetTopology.noAgentBody", { name: data.thisServer.machineName })}
            </span>
          </div>
        ) : null}

        {/* Services the product believes in that belong to no machine at all. Rare, and never silent. */}
        {s && s.unplacedServices > 0 ? (
          <div className="asf-alert" style={{ color: "#92400e", background: "#fef3c7", borderColor: "#fde68a" }}>
            <span style={{ fontSize: 16 }}>⚠</span>
            <span>{t("fleetTopology.unplacedServices", { count: s.unplacedServices })}</span>
          </div>
        ) : null}

        {/* Counts first: the question this page exists to answer is "how many machines, and is anything wrong". */}
        {s ? (
          <div className="asf-card">
            <div className="ftp-summary">
              <Tile label={t("fleetTopology.summaryMachines")} value={s.machines} />
              <Tile label={t("fleetTopology.summaryOnline")} value={`${s.online} / ${s.agents}`}
                    tone={s.agents === 0 ? "muted" : s.online < s.agents ? "warn" : "ok"} />
              <Tile label={t("fleetTopology.summarySeenNotRegistered")} value={s.seenNotRegistered}
                    tone={s.seenNotRegistered ? "warn" : "muted"} />
              <Tile label={t("fleetTopology.summaryRegisteredNotHeardFrom")} value={s.registeredNotHeardFrom}
                    tone={s.registeredNotHeardFrom ? "bad" : "muted"} />
              <Tile label={t("fleetTopology.summaryWithoutAgent")} value={s.withoutAgent ?? 0}
                    tone={s.withoutAgent ? "warn" : "muted"} />
              <Tile label={t("fleetTopology.summaryBehind")} value={s.versionsBehind}
                    tone={s.versionsBehind ? "warn" : "muted"} />
              <div className="ftp-summary__aside">
                <div><b>{t("fleetTopology.thisServer", { name: data.thisServer?.machineName, version: data.thisServer?.version })}</b></div>
                {fetchedAt ? <div>{t("fleetTopology.updatedAt", { time: fmtAbs(fetchedAt) })}</div> : null}
                <Link to="/admin/system-config" className="ftp-summary__link">{t("fleetTopology.goToUpdates")}</Link>
              </div>
            </div>
          </div>
        ) : null}

        {/* Filters */}
        <div className="asf-card">
          <div className="asf-grid">
            <div className="asf-field" style={{ flex: "2 1 240px" }}>
              <label className="asf-lbl">{t("common.search")}</label>
              <input className="asf-input" type="text" value={search}
                     placeholder={t("fleetTopology.searchPlaceholder")}
                     onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="asf-field" style={{ flex: "1 1 220px" }}>
              <label className="asf-lbl">{t("fleetTopology.filterState")}</label>
              <Dropdown value={stateFilter} onChange={setStateFilter} placeholder={t("fleetTopology.allStates")}
                options={[
                  { value: "", label: t("fleetTopology.allStates") },
                  { value: "Linked", label: t("fleetTopology.stateLinked") },
                  { value: "SeenNotRegistered", label: t("fleetTopology.stateSeenNotRegistered") },
                  { value: "RegisteredNotHeardFrom", label: t("fleetTopology.stateRegisteredNotHeardFrom") },
                  { value: "ReportingServiceOnly", label: t("fleetTopology.stateReportingServiceOnly") },
                ]} />
            </div>
            <div className="asf-field" style={{ flex: "0 0 auto", justifyContent: "flex-end" }}>
              <ToggleSwitch checked={offlineOnly} onChange={setOfflineOnly}
                            label={t("fleetTopology.offlineOnly")} />
            </div>
          </div>
        </div>

        {machines.length === 0 ? (
          <div className="asf-card">
            {/* The old hint here read "servers appear on their own, nothing needs to be configured" and
                was shown to an installation with three running servers. An empty list is now treated as a
                symptom to explain, not a state to wait out. */}
            <EmptyState icon="🖧"
              title={data ? t("fleetTopology.emptyTitle") : t("common.loading")}
              hint={!data ? ""
                    : total > 0 ? t("fleetTopology.emptyFiltered")
                    : t("fleetTopology.emptyHint")} />
          </div>
        ) : (
          <div className="ftp-grid">
            {machines.map((m) => (
              <MachineCard key={m.key} m={m} t={t} hostName={hostName}
                commands={commands.filter((c) => c.nodeRegistryId === m.nodeId
                                              || (!m.nodeId && c.hostConfigurationId === m.hostId))}
                onCommand={(service, command) => setPendingCmd({ machine: m, service, command })}
                onWithdraw={withdraw}
                onLink={(hostId, name) => setPendingLink({ node: m, hostId, hostName: name, unlink: false })}
                onUnlink={() => setPendingLink({ node: m, unlink: true })} />
            ))}
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!pendingLink}
        variant={pendingLink?.unlink ? "warn" : "info"}
        icon={pendingLink?.unlink ? "⛓" : "🔗"}
        title={pendingLink?.unlink ? t("fleetTopology.unlinkTitle") : t("fleetTopology.linkTitle")}
        name={pendingLink?.node?.displayName}
        body={pendingLink?.unlink
          ? t("fleetTopology.unlinkBody")
          : t("fleetTopology.linkBody", { host: pendingLink?.hostName })}
        confirmLabel={linking ? t("common.saving") : (pendingLink?.unlink ? t("fleetTopology.unlink") : t("fleetTopology.link"))}
        onConfirm={applyLink}
        onCancel={() => setPendingLink(null)}
      />

      {/* Acting on a machine is confirmed separately, and stopping something is coloured as what it is. */}
      <ConfirmModal
        open={!!pendingCmd}
        variant={pendingCmd?.command === "stop" ? "danger" : "warn"}
        icon={pendingCmd?.command === "stop" ? "■" : "▶"}
        title={t(`fleetTopology.confirm${pendingCmd?.command || "start"}Title`)}
        name={pendingCmd?.service}
        body={t("fleetTopology.confirmBody", { machine: pendingCmd?.machine?.displayName })}
        confirmLabel={sending ? t("common.saving") : t(`fleetTopology.cmd${pendingCmd?.command || "start"}`)}
        onConfirm={runCommand}
        onCancel={() => setPendingCmd(null)}
      />
    </div>
  );
}

function Tile({ label, value, tone }) {
  return (
    <div className={`ftp-tile${tone ? ` ftp-tile--${tone}` : ""}`}>
      <div className="ftp-tile__value">{value}</div>
      <div className="ftp-tile__label">{label}</div>
    </div>
  );
}

// Platform glyphs. A machine is recognised by shape before it is read by name, and a wall of identical
// cards is the thing that makes a fleet screen tiring to use.
const PLATFORM_GLYPH = {
  Windows: <><rect x="3" y="4" width="8" height="7" rx="1" /><rect x="13" y="3" width="8" height="8" rx="1" />
            <rect x="3" y="13" width="8" height="7" rx="1" /><rect x="13" y="13" width="8" height="8" rx="1" /></>,
  Linux:   <><path d="M12 3c2.2 0 3.4 1.9 3.4 4.3 0 1.6.6 2.4 1.5 3.6 1 1.3 1.8 2.4 1.8 4 0 2.7-2.9 4.1-6.7 4.1S5.3 17.6 5.3 14.9c0-1.6.8-2.7 1.8-4 .9-1.2 1.5-2 1.5-3.6C8.6 4.9 9.8 3 12 3Z" />
            <circle cx="10.3" cy="8" r="1" /><circle cx="13.7" cy="8" r="1" /></>,
  macOS:   <><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8M12 16v4" /></>,
};
function PlatformGlyph({ platform }) {
  const paths = PLATFORM_GLYPH[platform] || (
    <><rect x="2" y="4" width="20" height="7" rx="2" /><rect x="2" y="14" width="20" height="6" rx="2" />
      <path d="M6 7.5h.01M6 17h.01" /></>
  );
  return <div className="ftp-card__glyph"><svg viewBox="0 0 24 24" aria-hidden="true">{paths}</svg></div>;
}

const STATE_CLASS = {
  Linked: "linked",
  SeenNotRegistered: "seen",
  RegisteredNotHeardFrom: "unheard",
  ReportingServiceOnly: "reporting",
};
const STATE_PILL = {
  Linked: "asf-status--on",
  SeenNotRegistered: "asf-status--warn",
  RegisteredNotHeardFrom: "asf-status--err",
  // Neutral, not a warning. A server the product only knows through its own worker is the ordinary
  // state of an installation that was upgraded rather than reinstalled - it is under-equipped, not broken.
  ReportingServiceOnly: "asf-status--off",
};

export function MachineCard({ m, t, hostName, commands = [], onCommand, onWithdraw, onLink, onUnlink }) {
  // A machine can be acted on when something can reach it: its own agent, or a host row we SSH to.
  const canAct = !!onCommand && (m.agentPresent || m.hostId != null);
  const suggested = m.suggestedHostId != null ? hostName(m.suggestedHostId) : null;

  return (
    <div className={`ftp-card ftp-card--${STATE_CLASS[m.state] || "seen"}`}>
      <div className="ftp-card__head">
        <PlatformGlyph platform={m.platform} />
        <div className="ftp-card__ident">
          <div className="ftp-card__name" title={m.displayName}>{m.displayName}</div>
          <div className="ftp-card__seen">
            {m.online ? t("fleetTopology.online")
                      : m.lastHeartbeat ? t("fleetTopology.lastHeard", { time: fmtAbs(m.lastHeartbeat) })
                                        : t("fleetTopology.neverHeard")}
          </div>
        </div>
        <span className={`asf-status ${STATE_PILL[m.state] || "asf-status--off"}`}
              title={t(`fleetTopology.hint${m.state}`)}>
          <i className="asf-status__dot" />
          {t(`fleetTopology.state${m.state}`)}
        </span>
      </div>

      <div className="ftp-chips">
        {m.platform ? <span className="asf-chip ftp-chip--platform">{m.platform}</span> : null}
        {m.haMode === "secondary"
          ? <span className="asf-chip ftp-chip--standby" title={t("fleetTopology.haStandbyHint")}>
              {t("fleetTopology.haStandby")}
            </span>
          : null}
        {(m.components || []).map((c) => <span key={c} className="asf-chip">{c}</span>)}
      </div>

      <dl className="ftp-facts">
        <Row label={t("fleetTopology.version")}>
          {m.version || <span className="ftp-none">{t("fleetTopology.unknownVersion")}</span>}
          {m.upToDate === false
            ? <span className="asf-status asf-status--warn ftp-behind">{t("fleetTopology.behind")}</span>
            : null}
        </Row>
        <Row label={t("fleetTopology.address")}>
          {m.address || <span className="ftp-none">{t("fleetTopology.noAddress")}</span>}
        </Row>
        {/* The machine's own claim. Muted and marked: it is not somewhere anything connects to. */}
        {m.reportedAddress && !m.address ? (
          <Row label={t("fleetTopology.reportedAddress")}>
            <span className="ftp-claim" title={t("fleetTopology.reportedAddressHint")}>
              {m.reportedAddress}
              <span className="ftp-claim__tag">{t("fleetTopology.unverified")}</span>
            </span>
          </Row>
        ) : null}
        {m.installRoot ? (
          <Row label={t("fleetTopology.installRoot")}><code>{m.installRoot}</code></Row>
        ) : null}
      </dl>

      <div>
        <div className="ftp-svc__head">{t("fleetTopology.services")}</div>
        {(m.services || []).length === 0
          ? <div className="ftp-svc__none">{t("fleetTopology.noServices")}</div>
          : <div className="ftp-svc__list">
              {m.services.map((sv, i) => (
                <ServiceRow key={`${sv.name}-${i}`} sv={sv} t={t}
                            onCommand={canAct ? (cmd) => onCommand(sv.name, cmd) : undefined} />
              ))}
            </div>}
      </div>

      {commands.length > 0 ? <RecentCommands commands={commands} t={t} onWithdraw={onWithdraw} /> : null}

      {/* The operator's half of the reconciliation. */}
      <div className="ftp-foot">
        {m.state === "Linked" && m.nodeId ? (
          <button className="ftp-btn ftp-btn--unlink" onClick={onUnlink}>{t("fleetTopology.unlink")}</button>
        ) : suggested ? (
          <button className="ftp-btn ftp-btn--link" onClick={() => onLink(m.suggestedHostId, suggested)}>
            {t("fleetTopology.linkTo", { host: suggested })}
          </button>
        ) : m.state === "SeenNotRegistered" ? (
          <div className="ftp-foot__note">{t("fleetTopology.noHostToLink")}</div>
        ) : m.state === "ReportingServiceOnly" ? (
          // Not a warning about the machine - a statement about what WE lack. Nothing here can be
          // started, stopped or updated until this machine has an agent or a host entry.
          <div className="ftp-foot__note">{t("fleetTopology.noAgentOnMachine")}</div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * What has been asked of this machine lately. Without it a queued command is invisible until it has
 * finished — and the interesting cases are precisely the ones that do not finish: a machine that never
 * picks a command up, one that fails the same thing twice, one whose command expired unheeded.
 */
function RecentCommands({ commands, t, onWithdraw }) {
  const shown = commands.slice(0, 4);
  return (
    <div>
      <div className="ftp-svc__head">{t("fleetTopology.recent")}</div>
      <div className="ftp-cmd__list">
        {shown.map((c) => (
          <div key={c.id} className={`ftp-cmd ftp-cmd--${String(c.status).toLowerCase()}`}>
            <span className="ftp-cmd__what">
              {t(`fleetTopology.cmd${c.command}`)}{c.argument ? ` ${c.argument}` : ""}
            </span>
            <span className="ftp-cmd__status">{t(`fleetTopology.status${c.status}`)}</span>
            {c.resultMessage ? <span className="ftp-cmd__msg" title={c.resultMessage}>{c.resultMessage}</span> : null}
            <span className="ftp-cmd__who" title={t("fleetTopology.queuedBy", { user: c.queuedBy })}>{c.queuedBy}</span>
            {/* Only while it is still Pending: once an agent has claimed it the work may already be
                under way, and the server refuses. A button that can only return a conflict is worse
                than no button. */}
            {onWithdraw && c.status === "Pending" ? (
              <button className="ftp-cmd__x" title={t("fleetTopology.withdraw")}
                      onClick={() => onWithdraw(c.id)}>✕</button>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (<><dt>{label}</dt><dd>{children}</dd></>);
}

/**
 * One service, showing both accounts of it. The service manager says one thing and the product's own
 * registry says another often enough that picking one would hide the most useful signal on the page:
 * a worker that died without updating its row reads Running in the registry and Stopped on the box.
 */
export function ServiceRow({ sv, t, onCommand }) {
  const disagrees = sv.registryState
    && ((sv.registryState === "Running") !== (sv.state === "Running"))
    && sv.state !== "Unknown";
  const key = String(sv.state || "").toLowerCase();

  return (
    <div className={`ftp-svc${disagrees ? " ftp-svc--clash" : ""}`}>
      <i className={`ftp-svc__dot ftp-svc__dot--${key}`} />
      <span className="ftp-svc__name" title={sv.name}>{sv.name}</span>
      {sv.disabled ? (
        <span className="ftp-tag--disabled" title={t("fleetTopology.disabledHint")}>
          {t("fleetTopology.disabled")}
        </span>
      ) : null}
      <span className={`ftp-svc__state ftp-svc__state--${key}`}>{t(`fleetTopology.svc${sv.state}`)}</span>
      {disagrees ? (
        <span className="ftp-svc__clash" title={t("fleetTopology.disagreesHint")}>
          ⚠ {t("fleetTopology.registrySays", { state: t(`fleetTopology.svc${sv.registryState}`) })}
        </span>
      ) : null}
      {/* Only a service this product installs, and only where something can reach the machine. A row the
          agent never saw has no unit to act on. */}
      {onCommand && sv.seenByAgent && String(sv.name || "").startsWith("depository-") ? (
        <span className="ftp-svc__acts">
          {sv.state !== "Running" ? (
            <button className="ftp-act" title={t("fleetTopology.cmdstart")}
                    onClick={() => onCommand("start")}>▶</button>
          ) : null}
          {sv.state === "Running" ? (
            <button className="ftp-act" title={t("fleetTopology.cmdstop")}
                    onClick={() => onCommand("stop")}>■</button>
          ) : null}
          <button className="ftp-act" title={t("fleetTopology.cmdrestart")}
                  onClick={() => onCommand("restart")}>⟳</button>
        </span>
      ) : null}
    </div>
  );
}
